// app/javascript/components/AuditLog.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { t } from '../utils/i18n';
import Pagination from './Pagination';
import CommonModal from './Modal';


const AuditLog = ({ onBack, api }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [currentFilters, setCurrentFilters] = useState({
    resource_type: '',
    action_type: '',
    user_id: '',
    date: ''
  });

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/audit_logs');
      setAuditLogs(response.data.audit_logs || []);
      setFilters(response.data.filters || {});
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      setAuditLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogDetails = async (logId) => {
    try {
      const response = await api.get(`/audit_logs/${logId}`);
      setSelectedLog(response.data.audit_log);
    } catch (error) {
      console.error('Error fetching log details:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setCurrentFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

const filteredAuditLogs = useMemo(() => {
  return auditLogs.filter(log => {
    const matchesResourceType = !currentFilters.resource_type || log.resource_type === currentFilters.resource_type;
    const matchesActionType = !currentFilters.action_type || log.action_type === currentFilters.action_type;
    const matchesUser = !currentFilters.user_id || log.user === currentFilters.user_id;

    let matchesDate = true;
    if (currentFilters.date) {
      const logDate = new Date(log.created_at).toISOString().split('T')[0];
      matchesDate = logDate === currentFilters.date;
    }

    return matchesResourceType && matchesActionType && matchesUser && matchesDate;
  });
}, [auditLogs, currentFilters]);


  const paginatedAuditLogs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAuditLogs.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAuditLogs, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAuditLogs.length / itemsPerPage);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusClass = (actionType) => {
    const classes = {
      created_action: 'leaveActive',
      updated_action: 'leaveActive',
      deleted_action: 'leaveRejected',
      login_action: 'leaveActive',
      logout_action: 'leaveRejected'
    };
    return classes[actionType] || 'leaveActive';
  };

  const getStatusText = (actionType) => {
    const statusMap = {
      created_action: 'Completed',
      updated_action: 'Completed',
      deleted_action: 'Deleted',
      login_action: 'Success',
      logout_action: 'Completed'
    };
    return statusMap[actionType] || 'Completed';
  };

  return (
    <>
      <div className="mainContent">
        <div className="mainContentCT dBlock">
          <div className="baseTableWrap">
            <div className="dFlex baseTableHd mb_16">
              <h3 className="txt20 fwMedium colorBlack">{t('audit_trial')} ({filteredAuditLogs.length})</h3>
              <div className="dFlex baseTableTR">
                <div className="formInpt">
                  <select
                    name="user"
                    value={currentFilters.user_id}
                    onChange={(e) => handleFilterChange('user_id', e.target.value)}
                  >
                    <option value="">User</option>
                    {filters.users?.map(user => (
                      <option key={user.id} value={user.email}>{user.email}</option>
                    ))}
                  </select>
                </div>

                <div className="formInpt">
                  <input
                    type="date"
                    value={currentFilters.date}
                    onChange={(e) => handleFilterChange('date', e.target.value)}
                    placeholder="Select Date"
                  />
                </div>
                <div className="formInpt">
                  <select
                    name="module"
                    value={currentFilters.resource_type}
                    onChange={(e) => handleFilterChange('resource_type', e.target.value)}
                  >
                    <option value="">Module</option>
                    {filters.resource_types?.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div className="formInpt">
                  <select
                    name="action-type"
                    value={currentFilters.action_type}
                    onChange={(e) => handleFilterChange('action_type', e.target.value)}
                  >
                    <option value="">Action Type</option>
                    {filters.action_types?.map(type => (
                      <option key={type} value={type}>
                        {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {loading && (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}

            <div className="baseTableContainer">
              <table className="baseTable baseTableDepartment">
                <thead>
                  <tr>
                    <th>{t('timestamp')}</th>
                    <th>{t('sidebar.user')}</th>
                    <th>{t('module')}</th>
                    <th>{t('action')}</th>
                    <th>{t('status')}</th>
                    <th style={{minWidth: "175px"}}>{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAuditLogs.length === 0 && !loading ? (
                    <tr>
                      <td colSpan="6" style={{textAlign: 'center', padding: '20px'}}>
                        {t('no_audit_logs_found')}
                      </td>
                    </tr>
                  ) : (
                    paginatedAuditLogs.map((log) => (
                      <tr key={log.id}>
                        <td>{formatDate(log.created_at)}</td>
                        <td>{log.user || 'System'}</td>
                        <td>{log.resource_type}</td>
                        <td>{log.action_description}</td>
                        <td>
                          <span className={getStatusClass(log.action_type)}>
                            {getStatusText(log.action_type)}
                          </span>
                        </td>
                        <td>
                          <div className="dFlex optBlock">
                            <button onClick={() => fetchLogDetails(log.id)} className="optBtn">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-eye" viewBox="0 0 16 16">
                                <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13 13 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5s3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5s-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z"/>
                                <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0"/>
                              </svg>
                              <span data-i18n="view">{t('view')}</span>
                            </button>
                            {/* {log.resource_id && (
                              <>
                                <span className="bar">|</span>
                                <button type="button" className="optBtn">
                                  <img src="/images/edit.png" alt=""/>
                                  <span>Details</span>
                                </button>
                              </>
                            )} */}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage}/>
          </div>
        </div>
      </div>

      <CommonModal
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        title={t('audit_detail')}
        size="large"
      >
        {selectedLog && (
          <>
            <div className="details-grid" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div><strong>{t('time')}:</strong> {formatDate(selectedLog.created_at)}</div>
              <div><strong>{t('sidebar.user')}:</strong> {selectedLog.user || t('system')}</div>
              <div><strong>{t('action')}:</strong> {selectedLog.action_description}</div>
              <div><strong>{t('resource')}:</strong> {selectedLog.resource_type} #{selectedLog.resource_id}</div>
              <div><strong>{t('ip_address')}:</strong> {selectedLog.ip_address}</div>
              <div><strong>{t('location')}:</strong> {selectedLog.location || '-'}</div>
            </div>

            {selectedLog.changes && Object.keys(selectedLog.changes).length > 0 && (
              <div>
                <strong>{t('changes_made')}:</strong>
                <pre style={{
                  backgroundColor: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '4px',
                  marginTop: '8px',
                  fontSize: '12px',
                  overflowX: 'auto'
                }}>
                  {JSON.stringify(selectedLog.changes, null, 2)}
                </pre>
              </div>
            )}
          </>
        )}
      </CommonModal>
    </>
  );
};

export default AuditLog;
